import * as React from 'react'
import { useFormikContext } from 'formik'
import TextInputFieldFormik from 'uiComponents/input/textInput/textInputFieldFormik'
import { Reveal } from 'uiComponents/reveal/reveal'
import { SkeletonBar } from 'uiComponents/skeleton/skeleton'
import { CapacityFormValues } from 'capacity/staging/savePlan'
import { CapacityHealth } from 'capacity/types'
import { parseLimitInput } from 'capacity/domain/limit'
import { CountingTypeCards } from './countingTypeCards'
import { useTranslation } from 'translations'

interface CapacityConfigCardProps {
    loading: boolean
    isNew: boolean
    health: CapacityHealth
}

export function CapacityConfigCard({ loading, isNew, health }: CapacityConfigCardProps) {
    const { t } = useTranslation()
    const { values, setFieldValue } = useFormikContext<CapacityFormValues>()
    const limit = parseLimitInput(values.limit)

    return (
        <section className="capacity-card capacity-config" aria-label={t('capacity.config.heading')}>
            {loading ? (
                <div className="capacity-config-skeleton" aria-busy="true">
                    <SkeletonBar width="40%" height="1rem" />
                    <SkeletonBar width="100%" height="2.5rem" />
                    <SkeletonBar width="30%" height="1rem" />
                    <SkeletonBar width="100%" height="6rem" />
                </div>
            ) : (
                <>
                    <Reveal open={health === 'noDefaultRule'}>
                        <p className="capacity-note capacity-note-warning">{t('capacity.config.noDefaultRule')}</p>
                    </Reveal>
                    <div className="capacity-fields-row">
                        <div className="capacity-field">
                            <label className="capacity-field-label" htmlFor="capacity-name">
                                {t('capacity.config.name')}
                            </label>
                            <p className="capacity-field-help">{t('capacity.config.nameHelp')}</p>
                            <TextInputFieldFormik
                                id="capacity-name"
                                name="name"
                                placeholder={t('capacity.config.namePlaceholder')}
                                validateOnChange
                            />
                        </div>
                        <div className="capacity-field">
                            <label className="capacity-field-label" htmlFor="capacity-limit">
                                {t('capacity.config.limit')}
                            </label>
                            <p className="capacity-field-help">{t('capacity.config.limitHelp')}</p>
                            <TextInputFieldFormik
                                id="capacity-limit"
                                name="limit"
                                inputMode="numeric"
                                placeholder={t('capacity.config.limitPlaceholder')}
                                validateOnChange
                            />
                            <Reveal open={limit !== 'invalid' && limit.kind === 'closed'}>
                                <p className="capacity-note">{t('capacity.config.closed')}</p>
                            </Reveal>
                        </div>
                    </div>
                    {isNew && (
                        <div className="capacity-field">
                            <span className="capacity-field-label" id="capacity-counting-label">
                                {t('capacity.config.howItCounts')}
                            </span>
                            <CountingTypeCards
                                value={values.countingType}
                                onChange={(next) => setFieldValue('countingType', next)}
                            />
                        </div>
                    )}
                </>
            )}
        </section>
    )
}
